// Prerendered `/community` — sharing tiers, groups, and the two use-cases. Prerender-safe + loader-free for the same reasons as series.tsx: this module's whole graph is eagerly imported in Node at prerender, so no store/Amplify/@shared/window here.
// Chrome (theme + nav + footer) comes from splash-chrome, which provides the ThemeProvider — this file must NOT also wrap `Mui`.
// Its <title> deliberately avoids "ai journal": that term belongs to `/` and two pages competing for it cannibalize each other. The phrase appears once, in hero body copy.
// Every user-facing string here is governed by the marketing ban list enforced by community.dom.test.tsx — clinical vocabulary is a regulatory risk on this page, not a style question. That regex is not the whole rule and `#couples` is where it fails: an OUTCOME claim ("watching each other grow") is an unsubstantiated benefit claim under FTC medical/health-claim scrutiny, and no ban word appears in it — say what the feature DOES, never what it does for the reader.
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MUILink from '@mui/material/Link';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
// MenuBookOutlined, not AutoStoriesOutlined: MUI bakes the icon's component name into a `data-testid` that survives the production build, so an "AutoStories" glyph puts the literal string "Stories" into the prerendered HTML — A13 says "Series" everywhere.
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import CasinoIcon from '@mui/icons-material/CasinoOutlined';
import Diversity3Icon from '@mui/icons-material/Diversity3Outlined';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { Band, SectionHeader, SectionBody, FeatureCard, CtaBand, accentColor } from './splash-sections';
import { metaDescription, splashMeta, ORIGIN, softwareApplicationLd } from './series-ld';
import type { Route } from './+types/community';

const TITLE = 'Shared Journaling, Groups & Collaborative Series — Gnothi';
const DESCRIPTION = metaDescription(
  'Share entries at the privacy tier you pick, join anonymous groups matched to what you write about, and see people you share with show up in your Series.',
);
const PATH = '/community';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi Community',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'SocialNetworkingApplication',
      featureList: ['Per-share privacy tiers', 'Anonymous groups', 'Groups matched by what you write about', 'Shared entries woven into your Series'],
    }),
  },
];

// The hero's proof element: what the OTHER person actually gets at each depth, previewing the `Tiers` band below. Glosses track `Sharing/Outbound/Form/Form.tsx`'s own tier descriptions, so re-word them together. Rendered as plain `Box`, never a MUI `Chip` or `Card` — both paint their own `palette.text.primary` over the navy wash regardless of what you nest (`Card/Card.js`, `Chip/Chip.js:83`), which is the trap splash-sections' OnDark note describes.
const HERO_TIERS = [
  ['Full', 'your words, as you wrote them'],
  ['Summary', 'the gist, not the entry'],
  ['Sensitive', 'their AI only, never listed'],
] as const;

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <PeopleIcon sx={{ fontSize: 48 }} />
        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          Some stories are better with other people in them
        </Typography>
        <Typography variant="h6" component="p" sx={{ fontWeight: 400, maxWidth: 720, opacity: 0.9 }}>
          Gnothi is an AI journal that writes your life as a Series. Share a tag with one person, or join a group of strangers who write about the same things you do - and pick, per share, how deep it goes.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 5 }} sx={{ justifyContent: 'center', flexWrap: 'wrap', pt: 1 }}>
          {HERO_TIERS.map(([tier, gloss]) => (
            <Box key={tier}>
              {/* `component="div"`: MUI maps `subtitle2` to a real <h6>, and these are labels, not headings — three of them here would land above every section's <h2> in the document outline. */}
              <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, letterSpacing: '0.04em' }}>{tier}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>{gloss}</Typography>
            </Box>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large" sx={{ px: 4 }}>
            Get started free
          </Button>
          {/* A plain `href`, never `component={RouterLink} to="#groups"` — that bakes `href="/community/#groups"` and full-page-navigates. */}
          <Button component="a" href="#groups" variant="outlined" color="inherit" size="large">
            See how groups work
          </Button>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Full-entry sharing is free, and so is joining and posting in groups. Starting your own group and the AI-summary tiers are on the paid plan.
        </Typography>
      </Stack>
    </Band>
  );
}

function SharingSeries() {
  return (
    <Band>
      <SectionHeader
        icon={<MenuBookIcon />}
        tone="themes"
        eyebrow="Why share at all"
        title="What sharing actually does to your story"
        deck="A shared entry does not just land in a list. It changes what Gnothi knows about your life."
      />
      <SectionBody>
        Every entry someone shares with you arrives stamped with their name and redacted to the tier they chose. Gnothi is told, in the prompt itself, that this is that person's own words and never yours - and that they are <em>real in your life</em>: when you write about someone who looks like the same person, it connects the two rather than guessing.
      </SectionBody>
      <SectionBody>
        So the people you share with stop being footnotes. They show up in the chapters your Series writes, from their own side of the same evening.{' '}
        <MUILink component={RouterLink} to="/series">This is where they show up →</MUILink>
      </SectionBody>
    </Band>
  );
}

// The price label in each tier card's `footer` action row. `component="div"` because MUI maps `subtitle2` to a real <h6>, which inside a FeatureCard would sit below that card's own <h3> in the document outline.
function Price({ paid = false, children }: { paid?: boolean; children: ReactNode }) {
  return <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, ...(paid ? null : { color: accentColor() }) }}>{children}</Typography>;
}

function Tiers() {
  return (
    <Band color="grey">
      <SectionHeader
        icon={<ShieldIcon />}
        tone="themes"
        title="Three privacy tiers, and you pick per share"
        deck="A share is scoped to the tags you pick - never your whole journal - and every tier feeds the other person's AI at its own depth."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Full" tone="themes" footer={<Price>Free</Price>}>
            Your entry, as you wrote it. It shows up in their entries list and their AI reads it in brief. The sharing page carries no payment gate at all.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Summary" tone="themes" footer={<Price paid>Paid plan</Price>}>
            An AI summary stands in for the entry. Still listed, still available to their AI, but the raw text stays yours.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Sensitive" tone="themes" footer={<Price paid>Paid plan</Price>}>
            Prompt-only, and never listed: their AI can draw on a confidential summary while the person themselves cannot read it. It goes to a named person only - a sensitive share cannot target a group.
          </FeatureCard>
        </Grid>
      </Grid>
      {/* `shares_delete_request` (services/routes/shares/index.ts) is UNGATED as of `dd4143a5c` while post/put gate on `requirePaidTier(privacyTier)`, and `Shares.delete` scopes to the owner's uid — so payment gates CREATING a summary/sensitive share, never revoking one. Do not extend this into any other claim about what the free tier gets. */}
      <SectionBody>
        Deleting a share revokes access immediately - the tag it created in their tag bar goes with it. Revoking is free for everyone: the paid plan gates the Summary and Sensitive tiers at the moment you create or edit them, never your ability to take a share back.
      </SectionBody>
    </Band>
  );
}

function Groups() {
  return (
    <Band id="groups">
      <SectionHeader
        icon={<GroupsIcon />}
        tone="themes"
        title="Groups, anonymous by construction"
        deck="Anonymity here is a property of the endpoint, not a checkbox you hope someone set."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="A petname per group" tone="themes">
            Joining mints you a per-group name like <em>brave-fox-4821</em>. The members API never returns real usernames to anyone - there is no setting to get this wrong.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="Roles, and a door" tone="themes">
            Owner, admin, member, banned. Private groups are invisible to non-members, and a non-member cannot read messages even in a public group.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="Reviewed in the background" tone="themes">
            Every post gets a background model check; abusive or spammy posts are pulled live, for everyone in the room including the poster.
          </FeatureCard>
        </Grid>
      </Grid>
      {/* Browsing, joining and posting really are free (`#1840`): `Setup/Routing.tsx`'s `/g` subtree and mobile's groups screen are both ungated, and only the create CTA reads `hasPaidAccess`. Never re-broaden this to "Groups are paid". */}
      <SectionBody>
        Browsing, joining and posting are free. The one payment covers starting a group of your own - creating it, inviting people, running it - along with every other paid feature.
      </SectionBody>
    </Band>
  );
}

function Matching() {
  return (
    <Band color="dark">
      <SectionHeader
        icon={<HubIcon />}
        tone="themes"
        title="Found by what you write about"
        deck="Not tags you picked from a menu, and not a topic directory. The actual shape of your writing."
      />
      <SectionBody>
        Your entries are embedded as vectors and averaged into one vector for you; a group's vector is the mean of its members'. Discovery ranks groups by cosine similarity to yours above a floor, so a group of people circling the same subject surfaces before a busier one that isn't.
      </SectionBody>
      <SectionBody>
        Two caveats. This <strong>improves as you write</strong> - your first visit is a list, not a match, and when embeddings aren't available yet the ranking quietly falls back to what's recent. And privacy decides reachability: <strong>matchable</strong> groups are discoverable this way, <strong>public</strong> ones are browsable by anyone, <strong>private</strong> ones are invite-only.
      </SectionBody>
    </Band>
  );
}

function Dnd() {
  return (
    <Band id="dnd">
      <SectionHeader
        icon={<CasinoIcon />}
        tone="themes"
        eyebrow="In practice"
        title="A campaign that writes itself"
        deck="Five people at a table, one shared world, and a Series that keeps the log for you."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="The table shares into one place" tone="themes">
            Each player makes a share scoped to the campaign tag and points it at the group; it fans out to every member. Petnames hold inside the group, which suits posting in character.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="A learning segment beside it" tone="themes">
            Segments are open to everyone, in either mode: Teach Me, Both Sides, Another Angle, Book Recs, Dreams, Mirror, Try It, Wisdom Tale. Run Teach Me on the real history your world borrows from, in parallel with the campaign.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Then it's a podcast" tone="themes">
            Turn the show's feed on and your campaign becomes a private podcast your table subscribes to in Apple or Spotify - a real RSS feed, ungated. Public pages on this site and the front-page directory are a further step, and gated.
          </FeatureCard>
        </Grid>
      </Grid>
      <SectionBody>
        {/* `presets.generated.ts` `table: "segments"`: nine rows, all `bestFor: "both"`, and `depth` the only `isCool: true` one. The retired "grand arc / trial or duel / heist / mystery / journey / quiet insight" motif catalog is in no bundle the picker reads. */}
        Depth - the one preset kept for accounts that have it enabled - meets what you are actually carrying rather than the campaign. The parts above need none of it.{' '}
        <MUILink component={RouterLink} to="/series#podcast-generator">See how a Series becomes audio →</MUILink>
      </SectionBody>
    </Band>
  );
}

function Couples() {
  return (
    <Band id="couples" color="grey">
      <SectionHeader
        icon={<Diversity3Icon />}
        tone="themes"
        eyebrow="In practice"
        title="Two people, one shared thread"
        deck="Mutual sharing, each side choosing its own tags and its own tier."
      />
      <SectionBody>
        Partners can share entries with each other - each of you choosing which tags to share, and at which tier. What you each write stays your own words; what the other person sees is scoped to exactly what you chose to share, and you can revoke it at any time.
      </SectionBody>
      <SectionBody>
        Your partner's shared entries arrive marked as theirs, never as yours, and when you both write about the same evening from your own side, the story you're each reading starts to hold both perspectives. Not a verdict on who was right - a shared perspective neither of you had alone.
      </SectionBody>
      <SectionBody>
        {/* The <em> is the `parable` charter's own frontmatter `description` VERBATIM (`shared/authorship/prompts/presets/stories/parable.mdx`). Requote it, don't restyle it. */}
        Some couples run it as a <strong>parable</strong>: a fiction Series where the two of you are characters in a wisdom-tale, and the week's small frictions come back as a traveler's encounter rather than a recap. Gnothi's own description of that charter is the pitch: <em>"An allegorical road country - waystations, crossings, and trials with a price named"</em>. It is a way of getting some distance on your own week, and a way of watching the two of you turn up as <strong>characters</strong> in it.
      </SectionBody>
    </Band>
  );
}

export default function Community() {
  // Band tones alternate with no repeat across a seam: dark · light · grey · light · dark · light · grey · light. `Matching` spends the page's one dark accent — it is prose-only (no `<Card>`, no `text.primary` span), so `SectionBody` flips the muted role to grey.300 for free.
  return (
    <SplashChrome active="community">
      <Hero />
      <SharingSeries />
      <Tiers />
      <Groups />
      <Matching />
      <Dnd />
      <Couples />
      <CtaBand
        color="light"
        title="Start with one shared tag"
        deck="See what the story does with it. Full-entry sharing is free, and a share only ever reaches the tags you picked - never your whole journal."
      />
    </SplashChrome>
  );
}
